import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRightOutlined,
  EnvironmentOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  TagsOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import adminApi from "../api/adminApi";

function StatCard({ icon, label, value, detail, tone }) {
  return (
    <article className="admin-stat-card">
      <div className={`admin-stat-icon ${tone}`}>{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([adminApi.getProjects(), adminApi.getProjectCategories()])
      .then(([projectData, categoryData]) => {
        setProjects(projectData || []);
        setCategories(categoryData || []);
      })
      .catch((err) =>
        setError(adminApi.getErrorMessage(err, "Could not load dashboard")),
      )
      .finally(() => setLoading(false));
  }, []);

  const recent = useMemo(
    () =>
      [...projects]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 5),
    [projects],
  );

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="admin-page-stack">
      {error && <div className="admin-alert error">{error}</div>}
      <section className="admin-welcome">
        <div>
          <span className="admin-eyebrow">Website command center</span>
          <h2>Good to see you.</h2>
          <p>
            Keep project details, installation images, and categories accurate
            from one place.
          </p>
        </div>
        <Link to="/admin/projects/new" className="admin-primary-button">
          <PlusOutlined /> Add installation
        </Link>
      </section>
      <section className="admin-stats">
        <StatCard
          icon={<FolderOpenOutlined />}
          label="Total projects"
          value={projects.length}
          detail="Published installations"
          tone="blue"
        />
        <StatCard
          icon={<TagsOutlined />}
          label="Categories"
          value={categories.length}
          detail="Project classifications"
          tone="gold"
        />
        <StatCard
          icon={<ThunderboltOutlined />}
          label="With capacity"
          value={projects.filter((p) => p.capacity).length}
          detail="Capacity documented"
          tone="green"
        />
        <StatCard
          icon={<EnvironmentOutlined />}
          label="Locations"
          value={new Set(projects.map((p) => p.location).filter(Boolean)).size}
          detail="Areas represented"
          tone="purple"
        />
      </section>
      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h3>Recent projects</h3>
            <p>The latest additions to your website</p>
          </div>
          <Link to="/admin/projects" className="admin-text-link">
            View all <ArrowRightOutlined />
          </Link>
        </div>
        {recent.length ? (
          <div className="admin-recent-list">
            {recent.map((project) => (
              <Link
                to={`/admin/projects/${project._id}/edit`}
                key={project._id}
                className="admin-recent-row"
              >
                <div className="admin-project-thumb">
                  {project.images?.[0] ? (
                    <img src={project.images[0]} alt="" />
                  ) : (
                    <FolderOpenOutlined />
                  )}
                </div>
                <div className="admin-project-main">
                  <strong>{project.projectId || "No project ID"}</strong>
                  <span>{project.type}</span>
                </div>
                <div className="admin-project-meta">
                  <span>{project.location || "Location not set"}</span>
                  <small>{formatDate(project.createdAt)}</small>
                </div>
                <ArrowRightOutlined />
              </Link>
            ))}
          </div>
        ) : (
          <EmptyProjects />
        )}
      </section>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "Date unavailable";
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function EmptyProjects() {
  return (
    <div className="admin-empty">
      <FolderOpenOutlined />
      <h3>No projects yet</h3>
      <p>Create your first installation to start building the Project page.</p>
      <Link to="/admin/projects/new" className="admin-primary-button">
        Create project
      </Link>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="admin-page-stack">
      <div className="admin-skeleton hero" />
      <div className="admin-stats">
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className="admin-skeleton stat" />
        ))}
      </div>
      <div className="admin-skeleton table" />
    </div>
  );
}
